"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_BASE_ROLLUP_AGGREGATIONS = exports.AppBaseRelationProjectionError = void 0;
exports.appBaseRelationReads = appBaseRelationReads;
exports.appBaseTableChange = appBaseTableChange;
exports.appBaseTablesShowingChange = appBaseTablesShowingChange;
exports.resolvePortableAppBaseRollupGrouping = resolvePortableAppBaseRollupGrouping;
exports.projectPortableAppBaseRelations = projectPortableAppBaseRelations;
const database_identity_1 = require("@streamlain/database-identity");
class AppBaseRelationProjectionError extends Error {
    constructor(code) {
        super(code === 'APP_BASE_RELATION_DEPTH_EXCEEDED'
            ? 'App relation depth exceeded'
            : 'App relation row budget exceeded');
        this.code = code;
        this.name = 'AppBaseRelationProjectionError';
    }
}
exports.AppBaseRelationProjectionError = AppBaseRelationProjectionError;
/** The aggregations a rollup column computes its value with. */
exports.APP_BASE_ROLLUP_AGGREGATIONS = new Set([
    'show_original',
    'show_unique',
    'count',
    'count_values',
    'count_unique',
    'sum',
    'average',
    'min',
    'max',
    'percent_empty',
    'percent_filled',
    'concatenate',
]);
function relationTarget(column) {
    const target = column.data?.['targetDatabaseId'] ?? column.data?.['targetSourceKey'];
    return typeof target === 'string' ? target : null;
}
/**
 * What a read of `table` reads through its relations, following the cells
 * `projectPortableAppBaseRelations` computes (same order, same cycle rule):
 * every relation of `table` itself, and of each table reached the relations
 * and rollups whose fields a rollup reads. `tables` (the table first) are the
 * tables the read loads; its state is theirs. `shows` are the tables whose
 * values a rollup reads: a change of one of them changes what the read shows.
 * A relation cell alone holds the ids it links, which change only with a
 * write of its own table (a link and a removal write both sides), so the
 * tables only linked are not in `shows`. `fields` names, of each table in
 * `shows`, the fields its rollups read: `values` those whose values they
 * compute with, `presence` those whose cell a count only needs to exist
 * (the projection counts a linked row that holds the field).
 * `readsItself`: a table reached is `table`. `columnsOf` gives the columns
 * of a table, `keyOf` the table a relation target (`targetDatabaseId` or
 * `targetSourceKey`) names.
 */
function appBaseRelationReads(table, columnsOf, keyOf = target => target) {
    const tables = [table];
    const shows = [];
    const fields = {};
    let readsItself = false;
    const visit = (key, read, path) => {
        const columns = columnsOf(key) ?? [];
        const rollups = columns.filter(column => column.type === 'rollup' && (!read || read.has(column.id)));
        for (const column of columns) {
            if (column.type !== 'relation')
                continue;
            const targetRead = new Set(rollups.flatMap(rollup => {
                const targetFieldId = rollup.data?.['targetFieldId'];
                return rollup.data?.['sourceRelationFieldId'] === column.id &&
                    typeof targetFieldId === 'string'
                    ? [targetFieldId]
                    : [];
            }));
            if (read &&
                !read.has(column.id) &&
                !rollups.some(rollup => rollup.data?.['sourceRelationFieldId'] === column.id))
                continue;
            const target = relationTarget(column);
            if (target === null)
                continue;
            const targetKey = keyOf(target);
            if (targetKey === table)
                readsItself = true;
            if (!tables.includes(targetKey))
                tables.push(targetKey);
            if (!targetRead.size)
                continue;
            if (!shows.includes(targetKey))
                shows.push(targetKey);
            const shown = (fields[targetKey] ??= {
                values: new Set(),
                presence: new Set(),
            });
            for (const rollup of rollups) {
                const targetFieldId = rollup.data?.['targetFieldId'];
                if (rollup.data?.['sourceRelationFieldId'] !== column.id ||
                    typeof targetFieldId !== 'string')
                    continue;
                if (rollup.data?.['aggregation'] === 'count')
                    shown.presence.add(targetFieldId);
                else
                    shown.values.add(targetFieldId);
            }
            if (!path.includes(targetKey))
                visit(targetKey, targetRead, [...path, key]);
        }
    };
    visit(table, null, []);
    return {
        tables,
        shows,
        fields: Object.fromEntries(Object.entries(fields).map(([key, { values, presence }]) => [
            key,
            { values: [...values], presence: [...presence] },
        ])),
        readsItself,
    };
}
/** What a write changed of a table, from its rows `before` and `after`. */
function appBaseTableChange(before, after) {
    const was = new Map((before?.rows ?? []).map(row => [row.id, row]));
    const now = after?.rows ?? [];
    const cells = new Set();
    const presence = new Set();
    let rows = was.size !== now.length;
    for (const row of now) {
        const old = was.get(row.id);
        if (!old) {
            rows = true;
            continue;
        }
        if (old === row)
            continue;
        const oldCells = old.cells ?? {};
        const newCells = row.cells ?? {};
        for (const field of new Set([
            ...Object.keys(oldCells),
            ...Object.keys(newCells),
        ])) {
            const had = Object.prototype.hasOwnProperty.call(oldCells, field);
            const has = Object.prototype.hasOwnProperty.call(newCells, field);
            if (had !== has)
                presence.add(field);
            else if (JSON.stringify(oldCells[field]?.value) !==
                JSON.stringify(newCells[field]?.value))
                cells.add(field);
        }
    }
    return { rows, cells, presence };
}
/** Whether a change of a table changes what rollups reading `fields` show. */
function rollupsSeeChange(fields, change) {
    if (!change || !fields || change.rows)
        return true;
    return ([...fields.values, ...fields.presence].some(field => change.presence.has(field)) || fields.values.some(field => change.cells.has(field)));
}
/**
 * Of `heard`, the tables whose reads show a change of `changed`: each that is
 * one of them or whose `readsOf` (`appBaseRelationReads(…)`) shows one whose
 * change (`changeOf`, unknown when absent) touches what its rollups read.
 */
function appBaseTablesShowingChange(heard, changed, readsOf, changeOf) {
    return [...heard].filter(table => {
        if (changed.includes(table))
            return true;
        const { shows, fields } = readsOf(table);
        return shows.some(shown => changed.includes(shown) &&
            rollupsSeeChange(fields[shown], changeOf(shown)));
    });
}
/**
 * Resolves the same stable-value group shapes registered by the native database
 * view: select/multi-select in BlockSuite group-by/define.ts and relation in the
 * StreamLain database group-by extension. Computed numeric/text rollups stay
 * ungroupable; only show_* over a relation or select target inherits grouping.
 */
function resolvePortableAppBaseRollupGrouping(input) {
    if (input.column.type !== 'rollup')
        return undefined;
    const aggregation = input.column.data?.['aggregation'];
    if (aggregation !== 'show_original' && aggregation !== 'show_unique') {
        return undefined;
    }
    const relationId = input.column.data?.['sourceRelationFieldId'];
    const targetFieldId = input.column.data?.['targetFieldId'];
    if (typeof relationId !== 'string' || typeof targetFieldId !== 'string') {
        return undefined;
    }
    const relation = input.sourceColumns.find(column => column.id === relationId && column.type === 'relation');
    if (!relation)
        return undefined;
    const target = input.targetColumns.find(column => column.id === targetFieldId);
    if (!target)
        return undefined;
    if (target.type === 'rollup')
        return target.grouping;
    if (!['relation', 'select', 'multi-select'].includes(target.type)) {
        return undefined;
    }
    const options = target.data?.['options'];
    return {
        type: target.type,
        ...(Array.isArray(options)
            ? {
                options: options.flatMap(option => {
                    if (!option || typeof option !== 'object')
                        return [];
                    const { id, value } = option;
                    return typeof id === 'string' && typeof value === 'string'
                        ? [{ id, value }]
                        : [];
                }),
            }
            : {}),
    };
}
/**
 * Recomputes relation and rollup cells from an admitted target-row loader.
 * Server and StreamKit use this exact implementation; the caller owns target
 * authorization and supplies only rows visible in its current scope.
 */
async function projectPortableAppBaseRelations(input) {
    return projectRelations(input, null);
}
function linkedIds(row, column) {
    const raw = row.cells[column.id]?.value;
    return Array.isArray(raw)
        ? raw.filter((value) => typeof value === 'string')
        : [];
}
/**
 * `rows` in consecutive parts, each linking at most `limit` rows through each
 * of `relations`; a row that alone links more exceeds the budget.
 */
function relatedRowParts(rows, relations, limit) {
    const parts = [];
    let part = [];
    let linked = relations.map(() => new Set());
    for (const row of rows) {
        const ids = relations.map(column => new Set(linkedIds(row, column)));
        if (ids.some(own => own.size > limit))
            throw new AppBaseRelationProjectionError('APP_BASE_RELATION_ROW_BUDGET_EXCEEDED');
        const fits = ids.every((own, index) => linked[index].size +
            [...own].filter(id => !linked[index].has(id)).length <=
            limit);
        if (!fits) {
            parts.push(part);
            part = [];
            linked = relations.map(() => new Set());
        }
        part.push(row);
        ids.forEach((own, index) => own.forEach(id => linked[index].add(id)));
    }
    if (part.length)
        parts.push(part);
    return parts;
}
/**
 * `read` names the cells the table above reads from these rows; null reads
 * every cell. A linked table is followed only for the cells a rollup reads
 * from it, so a row costs the tables it shows and not every table it reaches.
 */
async function projectRelations(input, read) {
    const path = input.path ?? [];
    const maxDepth = input.maxDepth ?? 8;
    const maxRelatedRows = input.maxRelatedRows ?? 5000;
    if (path.length >= maxDepth) {
        throw new AppBaseRelationProjectionError('APP_BASE_RELATION_DEPTH_EXCEEDED');
    }
    const rollups = input.columns.filter(column => column.type === 'rollup' && (!read || read.has(column.id)));
    const relations = input.columns.filter(column => column.type === 'relation' &&
        (!read ||
            read.has(column.id) ||
            rollups.some(rollup => rollup.data?.['sourceRelationFieldId'] === column.id)));
    // The rows go in parts of the same budget: each part links at most
    // `maxRelatedRows` rows through each relation; the rows keep their order.
    const parts = relatedRowParts(input.rows, relations, maxRelatedRows);
    if (parts.length > 1) {
        const projected = [];
        for (const rows of parts)
            projected.push(...(await projectRelations({ ...input, rows }, read)));
        return projected;
    }
    const result = input.rows.map(row => ({ ...row, cells: { ...row.cells } }));
    const related = new Map();
    for (const column of relations) {
        const target = relationTarget(column);
        const targetRead = new Set(rollups.flatMap(rollup => {
            const targetFieldId = rollup.data?.['targetFieldId'];
            return rollup.data?.['sourceRelationFieldId'] === column.id &&
                typeof targetFieldId === 'string'
                ? [targetFieldId]
                : [];
        }));
        const ids = [...new Set(input.rows.flatMap(row => linkedIds(row, column)))];
        const cycle = target !== null && path.includes(target);
        const loaded = target !== null && ids.length
            ? await input.loadTarget(target, ids)
            : null;
        const targetRows = loaded && target !== null
            ? !targetRead.size
                ? loaded.rows
                : cycle
                    ? loaded.rows.map(row => ({
                        ...row,
                        cells: Object.fromEntries(Object.entries(row.cells).filter(([id]) => !loaded.columns.some(candidate => candidate.id === id &&
                            (candidate.type === 'rollup' ||
                                candidate.type === 'relation')))),
                    }))
                    : await projectRelations({
                        ...loaded,
                        sourceId: target,
                        loadTarget: input.loadTarget,
                        path: [...path, input.sourceId],
                        maxDepth,
                        maxRelatedRows,
                    }, targetRead)
            : [];
        const byId = new Map(targetRows.map(row => [row.id, row]));
        related.set(column.id, byId);
        for (const row of result) {
            const raw = row.cells[column.id]?.value;
            row.cells[column.id] = {
                value: Array.isArray(raw)
                    ? raw.filter(id => typeof id === 'string' && byId.has(id))
                    : [],
            };
        }
    }
    for (const column of rollups) {
        const relationId = column.data?.['sourceRelationFieldId'];
        const targetFieldId = column.data?.['targetFieldId'];
        const aggregation = column.data?.['aggregation'];
        const separator = column.data?.['separator'];
        for (const row of result) {
            let value = null;
            if (typeof relationId === 'string' &&
                typeof targetFieldId === 'string' &&
                typeof aggregation === 'string' &&
                exports.APP_BASE_ROLLUP_AGGREGATIONS.has(aggregation)) {
                const ids = row.cells[relationId]?.value;
                const targets = related.get(relationId);
                if (Array.isArray(ids) && targets) {
                    const values = ids.flatMap(id => {
                        const target = typeof id === 'string' ? targets.get(id) : undefined;
                        return target &&
                            Object.prototype.hasOwnProperty.call(target.cells, targetFieldId)
                            ? [target.cells[targetFieldId].value]
                            : [];
                    });
                    value = (0, database_identity_1.computeRollupValue)(values, aggregation, typeof separator === 'string' ? separator : undefined);
                }
            }
            row.cells[column.id] = { value };
        }
    }
    return result;
}
